/**
 * WorkspaceSwitcher — the active-customer dropdown that sits in the admin
 * top bar (multi-customer, Phase 3).
 *
 * Shows the active customer; clicking opens a dropdown grouped by type
 * (dealerships / private), plus "View All" (consolidated) and "Manage
 * Customers" (jumps to Settings → Customers). Reads everything from
 * CustomerContext, so switching re-renders every page wired to the context.
 *
 * Hidden entirely when showSwitcher is false (e.g. a contractor with access
 * to a single customer) — there's nothing to switch.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useCustomer } from '../contexts/CustomerContext';

const NAVY   = '#1e3a4a';
const LINE   = '#e5e7eb';

// Group labels by customer.type.
const GROUPS = [
  { key: 'dealerships', label: '🏢 Dealerships', types: ['dealership_full', 'dealership_partial'] },
  { key: 'private',     label: '👤 Private Customers', types: ['private_pool', 'private_recurring'] },
];

export default function WorkspaceSwitcher({ onManage }) {
  const { customers, activeCustomer, activeId, isViewAll, showSwitcher, setActiveId, loading } = useCustomer();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside-click.
  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  if (!showSwitcher || loading) return null;

  const label = isViewAll
    ? '🌐 View All'
    : (activeCustomer ? activeCustomer.name : 'Select workspace');
  const dot = !isViewAll && activeCustomer?.color_hex ? activeCustomer.color_hex : NAVY;

  function pick(id) { setActiveId(id); setOpen(false); }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} style={{
        display: 'flex', alignItems: 'center', gap: 8,
        background: '#fff', border: `1px solid ${LINE}`, borderRadius: 10,
        padding: '7px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 700,
        color: NAVY, boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      }}>
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: dot, flex: 'none' }} />
        <span style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </span>
        <span style={{ fontSize: 10, color: '#94a3b8' }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 6, zIndex: 200,
          background: '#fff', border: `1px solid ${LINE}`, borderRadius: 12,
          boxShadow: '0 12px 32px rgba(15,23,42,0.16)', minWidth: 280, overflow: 'hidden',
        }}>
          <div style={{
            padding: '10px 14px 8px', fontSize: 10.5, fontWeight: 800,
            letterSpacing: 0.8, textTransform: 'uppercase', color: '#94a3b8',
          }}>Active workspace</div>

          {GROUPS.map(group => {
            const items = customers.filter(c => group.types.includes(c.type));
            if (items.length === 0) return null;
            return (
              <div key={group.key}>
                <div style={{ padding: '6px 14px 2px', fontSize: 11, fontWeight: 700, color: '#64748b' }}>
                  {group.label}
                </div>
                {items.map(c => {
                  const isActive = String(c.id) === String(activeId) && !isViewAll;
                  return (
                    <button key={c.id} onClick={() => pick(String(c.id))} style={rowStyle(isActive)}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: c.color_hex || NAVY, flex: 'none' }} />
                      <span style={{ flex: 1, textAlign: 'left' }}>{c.name}</span>
                      {c.is_default ? <span style={tag()}>main</span> : null}
                      {isActive ? <span style={{ color: '#16a34a', fontWeight: 800 }}>✓</span> : null}
                    </button>
                  );
                })}
              </div>
            );
          })}

          <div style={{ borderTop: `1px solid ${LINE}`, marginTop: 6 }}>
            <button onClick={() => pick('all')} style={rowStyle(isViewAll)}>
              <span style={{ flex: 1, textAlign: 'left' }}>🌐 View All (consolidated)</span>
              {isViewAll ? <span style={{ color: '#16a34a', fontWeight: 800 }}>✓</span> : null}
            </button>
            {onManage && (
              <button onClick={() => { setOpen(false); onManage(); }} style={rowStyle(false)}>
                <span style={{ flex: 1, textAlign: 'left' }}>⚙️ Manage Customers</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const rowStyle = (active) => ({
  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
  padding: '9px 14px', border: 'none', cursor: 'pointer', fontSize: 13,
  background: active ? '#f1f5f9' : 'transparent',
  color: '#0f172a', fontWeight: active ? 700 : 500,
});
const tag = () => ({
  fontSize: 10, fontWeight: 700, color: '#0369a1', background: '#e0f2fe',
  padding: '1px 6px', borderRadius: 999,
});
